import { useState, useRef, useEffect } from 'react';
import { ThumbsUp, ThumbsDown, SkipForward, Check, Pencil, X, Send } from 'lucide-react';
import { _t } from '@/i18n/t';

interface TrainingFeedbackProps {
  question: string;
  assistantResponse: string;
  conversationHistory: Array<{ role: string; content: string }>;
  metadata?: {
    sources?: string[];
    [key: string]: unknown;
  };
  onFeedbackSubmitted: (type: 'positive' | 'negative' | 'skip') => void;
}

type FeedbackState = 'pending' | 'positive' | 'negative' | 'skip' | 'correcting';

const TrainingFeedback = ({
  question,
  assistantResponse,
  conversationHistory,
  metadata,
  onFeedbackSubmitted,
}: TrainingFeedbackProps) => {
  const [state, setState] = useState<FeedbackState>('pending');
  const [correctedResponse, setCorrectedResponse] = useState('');
  const [trainerNotes, setTrainerNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (state === 'correcting' && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [state]);

  const handleThumbsUp = async () => {
    setIsSubmitting(true);
    try {
      const { callUIAPI } = await import('../../utils/apiUtils');
      const { METHOD_POST } = await import('../../utils/apiUtilsConstants');

      await callUIAPI(
        'assistant/training/correction',
        {
          question,
          conversationContext: conversationHistory,
          retrievedChunkIds: [],
          originalResponse: assistantResponse,
          correctedResponse: null,
          rating: 'positive',
          trainerNotes: null,
          metadata: metadata || {},
        },
        METHOD_POST,
        true
      );

      setState('positive');
      onFeedbackSubmitted('positive');
    } catch (err) {
      console.error('Failed to submit positive feedback:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleThumbsDown = () => {
    setState('correcting');
  };

  const handleSkip = () => {
    setState('skip');
    onFeedbackSubmitted('skip');
  };

  const handleCancelCorrection = () => {
    setState('pending');
    setCorrectedResponse('');
    setTrainerNotes('');
  };

  const handleSubmitCorrection = async () => {
    if (!correctedResponse.trim()) return;

    setIsSubmitting(true);
    try {
      const { callUIAPI } = await import('../../utils/apiUtils');
      const { METHOD_POST } = await import('../../utils/apiUtilsConstants');

      await callUIAPI(
        'assistant/training/correction',
        {
          question,
          conversationContext: conversationHistory,
          retrievedChunkIds: [],
          originalResponse: assistantResponse,
          correctedResponse: correctedResponse.trim(),
          rating: 'negative',
          trainerNotes: trainerNotes.trim() || null,
          metadata: metadata || {},
        },
        METHOD_POST,
        true
      );

      setState('negative');
      onFeedbackSubmitted('negative');
    } catch (err) {
      console.error('Failed to submit correction:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTextareaInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 160) + 'px';
  };

  if (state === 'positive') {
    return (
      <div className="flex items-center gap-1.5 mt-1 ml-1">
        <Check className="w-3 h-3 text-emerald-500" />
        <span className="text-[11px] text-emerald-600">Marked as good</span>
      </div>
    );
  }

  if (state === 'negative') {
    return (
      <div className="flex items-center gap-1.5 mt-1 ml-1">
        <Pencil className="w-3 h-3 text-amber-500" />
        <span className="text-[11px] text-amber-600">Correction submitted</span>
      </div>
    );
  }

  if (state === 'skip') {
    return null;
  }

  if (state === 'correcting') {
    return (
      <div className="mt-2 ml-1 mr-2">
        <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-2.5">
          <p className="text-[11px] text-amber-700 font-medium mb-1.5">
            Write the response you'd prefer:
          </p>
          <textarea
            ref={textareaRef}
            data-testid="training-feedback-corrected-response"
            aria-label={_t('aria.correctedResponse')}
            value={correctedResponse}
            onChange={(e) => setCorrectedResponse(e.target.value)}
            onInput={handleTextareaInput}
            placeholder="Type the corrected response…"
            rows={3}
            className="w-full resize-none bg-white border border-amber-200 rounded-md px-2.5 py-2 text-[12px] text-[hsl(222_47%_11%)] placeholder:text-[hsl(215_20%_65%)] focus:outline-none focus:ring-1 focus:ring-amber-300 focus:border-amber-300"
            style={{ maxHeight: '160px', minHeight: '64px' }}
            disabled={isSubmitting}
          />
          <input
            type="text"
            data-testid="training-feedback-trainer-notes"
            aria-label={_t('aria.trainerNotes')}
            value={trainerNotes}
            onChange={(e) => setTrainerNotes(e.target.value)}
            placeholder="Notes (optional) — why is this correction needed?"
            className="w-full mt-1.5 bg-white border border-amber-200 rounded-md px-2.5 py-1.5 text-[11px] text-[hsl(222_47%_11%)] placeholder:text-[hsl(215_20%_65%)] focus:outline-none focus:ring-1 focus:ring-amber-300 focus:border-amber-300"
            disabled={isSubmitting}
          />
          <div className="flex items-center justify-end gap-2 mt-2">
            <button
              type="button"
              onClick={handleCancelCorrection}
              disabled={isSubmitting}
              className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              <X className="w-3 h-3" />
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmitCorrection}
              disabled={!correctedResponse.trim() || isSubmitting}
              className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: correctedResponse.trim() && !isSubmitting ? 'hsl(25 95% 53%)' : 'hsl(25 30% 75%)' }}
            >
              <Send className="w-3 h-3" />
              Submit correction
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 mt-1.5 ml-1">
      <button
        type="button"
        onClick={handleThumbsUp}
        disabled={isSubmitting}
        className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors disabled:opacity-50"
        title="Good response"
      >
        <ThumbsUp className="w-3 h-3" />
      </button>
      <button
        type="button"
        onClick={handleThumbsDown}
        disabled={isSubmitting}
        className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] text-gray-400 hover:text-amber-600 hover:bg-amber-50 transition-colors disabled:opacity-50"
        title="Needs correction"
      >
        <ThumbsDown className="w-3 h-3" />
      </button>
      <button
        type="button"
        onClick={handleSkip}
        disabled={isSubmitting}
        className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
        title="Skip"
      >
        <SkipForward className="w-3 h-3" />
      </button>
    </div>
  );
};

export default TrainingFeedback;